import React from 'react';

const ValidationErrorBanner = ({
  errorCount,
  onDismiss,
}: {
  errorCount: number;
  onDismiss: () => void;
}) => (
  <div
    role="alert"
    className="tw-flex tw-items-center tw-justify-between tw-rounded-lg tw-border tw-border-solid tw-border-red-300 tw-bg-red-50 tw-px-4 tw-py-3 tw-mb-4"
  >
    <div className="tw-flex tw-items-center tw-gap-2">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="tw-h-5 tw-w-5 tw-text-unm-error-red tw-flex-shrink-0"
        viewBox="0 0 20 20"
        fill="currentColor"
      >
        <path
          fillRule="evenodd"
          d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
          clipRule="evenodd"
        />
      </svg>
      <span className="tw-text-sm tw-font-semibold tw-text-unm-error-red">
        Please correct {errorCount} {errorCount === 1 ? 'error' : 'errors'} to
        continue.
      </span>
    </div>
    <button
      type="button"
      onClick={onDismiss}
      className="tw-bg-transparent tw-border-none tw-text-red-400 tw-cursor-pointer tw-p-1 hover:tw-text-red-600"
      aria-label="Dismiss error banner"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="tw-h-4 tw-w-4"
        viewBox="0 0 20 20"
        fill="currentColor"
      >
        <path
          fillRule="evenodd"
          d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
          clipRule="evenodd"
        />
      </svg>
    </button>
  </div>
);

const FloatingValidationBanner = ({
  errorCount,
  onDismiss,
  setErrorCount,
}: {
  errorCount: number;
  onDismiss: () => void;
  setErrorCount: (count: number) => void;
}) => {
  const bannerRef = React.useRef<HTMLDivElement>(null);
  const onDismissRef = React.useRef(onDismiss);
  onDismissRef.current = onDismiss;
  const hasErrors = errorCount > 0;
  const scrolledRef = React.useRef(false);

  const smoothScrollToError = React.useCallback((errorEl: Element) => {
    const scrollContainer = document.getElementById('app-root-node');
    if (!scrollContainer) return;

    const target = errorEl.closest('.k-form-field-wrap')?.parentElement;
    if (!target) return;

    const targetY =
      target.getBoundingClientRect().top +
      scrollContainer.scrollTop -
      scrollContainer.getBoundingClientRect().top -
      90;
    const startY = scrollContainer.scrollTop;
    const distance = targetY - startY;
    const duration = 800;
    let startTime: number | null = null;

    const easeInOutCubic = (t: number) =>
      t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / duration, 1);
      scrollContainer.scrollTop = startY + distance * easeInOutCubic(progress);
      if (progress < 1) requestAnimationFrame(animate);
    };

    requestAnimationFrame(animate);
  }, []);

  // Reset scroll tracking when errors clear and reappear
  React.useEffect(() => {
    if (!hasErrors) scrolledRef.current = false;
  }, [hasErrors]);

  // Scroll to the first error when the banner first appears
  React.useEffect(() => {
    if (!hasErrors || scrolledRef.current) return;
    scrolledRef.current = true;

    const firstError = document.querySelector('[id$="_error"]');
    if (!firstError) return;

    requestAnimationFrame(() => smoothScrollToError(firstError));
  }, [hasErrors, smoothScrollToError]);

  // MutationObserver to live-update error count as user fixes fields
  React.useEffect(() => {
    if (!hasErrors) return;

    let previousErrorIds = new Set(
      Array.from(document.querySelectorAll('[id$="_error"]'))
        .filter((el) => !bannerRef.current?.contains(el))
        .map((el) => el.id),
    );

    const updateErrorCount = () => {
      const allErrors = document.querySelectorAll('[id$="_error"]');
      const realErrors = Array.from(allErrors).filter(
        (el) => !bannerRef.current?.contains(el),
      );
      const currentErrorIds = new Set(realErrors.map((el) => el.id));

      if (realErrors.length === 0) {
        onDismissRef.current();
      } else {
        // An error was fixed — scroll to the next one
        const anErrorWasFixed = Array.from(previousErrorIds).some(
          (id) => !currentErrorIds.has(id),
        );
        if (anErrorWasFixed) {
          smoothScrollToError(realErrors[0]);
        }
        setErrorCount(realErrors.length);
      }
      previousErrorIds = currentErrorIds;
    };

    const observer = new MutationObserver(updateErrorCount);
    const formFieldset = document.querySelector('.k-form-fieldset');
    if (formFieldset) {
      observer.observe(formFieldset, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    return () => observer.disconnect();
  }, [hasErrors, setErrorCount, smoothScrollToError]);

  if (!hasErrors) return null;

  return (
    <div ref={bannerRef} className="tw-sticky tw-top-3 tw-z-50">
      <ValidationErrorBanner errorCount={errorCount} onDismiss={onDismiss} />
    </div>
  );
};

export default FloatingValidationBanner;
